import {
  Flex,
  SegmentedControl,
  Divider,
  Tabs,
  Accordion,
  Box,
  Text,
  Button,
  Modal,
  TextInput,
  Radio,
  Stack,
  useMantineTheme,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import {
  FinancialCategory,
  CategoryInfo,
  categoryToDisplayName,
  financialAssetOptions,
  financialCategories,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import { ReactElement, useEffect, useState } from 'react';
import { ClientSearch } from 'src/UI/page-nav/ClientSearch';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useNavigate } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import {
  UpsertEntityInput,
  useUpsertEntity,
} from 'src/data-capture/merge-review/useUpsertEntities';
import useFinancialEntityLayout from 'src/UI/dynamicUI/hooks/useFinancialEntityLayout';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { notifications } from '@mantine/notifications';
import { useValidateProviderReference } from 'src/financial/financial-data/hooks/useValidateProviderReference';
import { useConnectAsset } from 'src/financial/financial-data/hooks/useConnectAsset';
import { Logger } from 'src/utils/Logger';

const providerReferenceSchema = z.object({
  providerReference: z
    .string()
    .min(1, 'Provider reference number is required')
    .max(36, 'Provider reference number must be 36 characters or less')
    .regex(
      /^[a-zA-Z0-9-]+$/,
      'Provider reference number must contain only letters, numbers, and hyphens',
    ),
});

type ProviderReferenceForm = z.infer<typeof providerReferenceSchema>;

type PickerProps = {
  onClose: () => void;
  showHeader?: boolean;
  isDrawer?: boolean;
};

export const CreateFinancialItemPicker = ({
  onClose,
  showHeader = true,
  isDrawer = false,
}: PickerProps) => {
  const [selectedCategory, setSelectedCategory] = useState<FinancialCategory>('SPENDING_ACCOUNT');
  const [tabValue, setTabValue] = useState<string | null>(null);
  const [choiceModalOpened, setChoiceModalOpened] = useState(false);
  const [providerModalOpened, setProviderModalOpened] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<'connection' | 'manually'>('connection');
  const [isUpserting, setIsUpserting] = useState(false);
  const mantineTheme = useMantineTheme();
  const navigate = useNavigate();
  const { clientId } = useCurrentClient();
  const { upsertEntity } = useUpsertEntity();
  const { layout, isLoading: isLoadingLayout } = useFinancialEntityLayout({
    entityType: tabValue || undefined,
  });

  const { connectAsset } = useConnectAsset({ clientId: clientId || '' });

  const navigateAndClose = (path: string) => {
    navigate(path);
    if (isDrawer) onClose();
  };

  const handleAddNewClick = () => {
    if (selectedCategory === 'ASSET') {
      setChoiceModalOpened(true);
    } else {
      navigateAndClose(`/client/${clientId}/financial-items/create/${tabValue}`);
    }
  };

  const handleContinue = () => {
    setChoiceModalOpened(false);
    if (selectedMethod === 'manually') {
      navigateAndClose(`/client/${clientId}/financial-items/create/${tabValue}`);
    } else {
      setProviderModalOpened(true);
    }
  };

  const handleProviderSubmit = async (providerReference: string) => {
    setProviderModalOpened(false);

    if (selectedCategory === 'ASSET' && tabValue && clientId && layout) {
      try {
        setIsUpserting(true);

        const metadata = layout.entitiesMetadata?.[tabValue];
        if (!metadata) {
          throw new Error(`No metadata found for entity type: ${tabValue}`);
        }

        const entityValues = {
          provider_reference_number: providerReference,
          is_updated_automatically: true,
        };

        const upsertInput: UpsertEntityInput = {
          entityFieldsMetadata: metadata,
          entityType: tabValue,
          values: entityValues,
        };
        const upsertedEntityId = await upsertEntity(clientId, upsertInput, true);

        if (upsertedEntityId) {
          try {
            await connectAsset(upsertedEntityId);

            notifications.show({
              title: 'Success',
              message: 'Asset connection created and populated successfully',
              color: 'green',
            });
          } catch (connectError) {
            Logger.warn('Failed to connect asset to feed after creation', {
              connectError,
              assetId: upsertedEntityId,
            });

            notifications.show({
              title: 'Partially successful',
              message:
                'Asset created successfully, but failed to connect to feed data. You can manually connect it later.',
              color: 'yellow',
            });
          }

          navigateAndClose(
            `/client/${clientId}/financial-items/${encodeURIComponent(tabValue)}/${encodeURIComponent(upsertedEntityId)}/`,
          );
        } else {
          throw new Error('Failed to create asset connection');
        }
      } catch (error) {
        notifyAndLogError(
          'Error creating asset connection',
          'Something went wrong trying to create the asset connection. Please try again and contact support if the issue persists.',
          error,
          { entityType: tabValue, providerReference },
        );
      } finally {
        setIsUpserting(false);
      }
    } else {
      navigateAndClose(
        `/client/${clientId}/financial-items/create/${encodeURIComponent(tabValue ?? '')}?providerReference=${encodeURIComponent(providerReference)}`,
      );
    }
  };

  const handleProviderModalClose = () => {
    setProviderModalOpened(false);
  };

  useEffect(() => {
    const categoryInfo = financialAssetOptions[selectedCategory];
    setTabValue(categoryInfo.productsOrder[0]);
  }, [selectedCategory]);

  const isCategoryInfo = (obj: unknown): boolean => {
    return !!obj && typeof obj === 'object' && 'products' in obj;
  };

  const renderTabs = (categoryInfo: CategoryInfo) => {
    return categoryInfo.productsOrder.map(product => {
      const productInfo = categoryInfo.products[product];
      if (isCategoryInfo(productInfo)) {
        return (
          <Accordion key={product} defaultValue={product} variant={'vertical-tabs'}>
            <Accordion.Item role="region" key={product} value={product}>
              <Accordion.Control>{productInfo.label}</Accordion.Control>
              <Accordion.Panel>
                <Tabs.List style={{ alignItems: 'flex-start' }}>
                  {renderTabs(productInfo as CategoryInfo)}
                </Tabs.List>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        );
      }

      return (
        <Tabs.Tab key={product} value={product}>
          {categoryInfo.products[product].label}
        </Tabs.Tab>
      );
    });
  };

  const renderTabContents = (categoryInfo: CategoryInfo) => {
    return categoryInfo.productsOrder.flatMap((product): ReactElement | ReactElement[] => {
      const productInfo = categoryInfo.products[product];
      if (isCategoryInfo(productInfo)) {
        return renderTabContents(productInfo as CategoryInfo);
      }

      const ProductIcon = categoryInfo.products[product].icon || categoryInfo.icon;
      return (
        <Tabs.Panel key={product} value={product}>
          <Box
            style={{
              position: 'absolute',
              height: '100%',
              width: 'auto',
              right: 0,
              bottom: '0%',
            }}
          >
            <ProductIcon
              color={mantineTheme.colors.gray[2]}
              style={{ height: '145%', width: 'auto' }}
            />
          </Box>
          <Text size={'displaySm'} fw={FONT_WEIGHTS.semibold}>
            {categoryInfo.products[product].label}
          </Text>
        </Tabs.Panel>
      );
    });
  };

  const categoryInfo = financialAssetOptions[selectedCategory];
  const isSingleProduct =
    categoryInfo.productsOrder.length === 1 &&
    !isCategoryInfo(categoryInfo.products[categoryInfo.productsOrder[0]]);

  return (
    <Flex direction="column" style={showHeader ? { flex: 1, minHeight: 0 } : undefined}>
      {showHeader && (
        <>
          <Flex justify={'flex-end'}>
            <IconX size={'20'} onClick={onClose} />
          </Flex>
          <ClientSearch prefix={'Add data for'} />
        </>
      )}
      <SegmentedControl
        withItemsBorders={false}
        value={selectedCategory}
        onChange={value => setSelectedCategory(value as FinancialCategory)}
        data={financialCategories.map(f => ({
          value: f,
          label:
            categoryToDisplayName[f as FinancialCategory] ||
            categoryToDisplayName['SPENDING_ACCOUNT'],
        }))}
        styles={{
          control: { width: '155px' },
        }}
        mt={'md'}
      />
      <Divider my={'xxl'} />

      <Tabs
        value={tabValue}
        onChange={setTabValue}
        orientation="vertical"
        variant={'vertical'}
        style={showHeader ? { flex: 1, minHeight: 0, height: '100%' } : undefined}
      >
        {!isSingleProduct && (
          <Tabs.List>
            <Box pr={'150px'}>{renderTabs(categoryInfo)}</Box>
          </Tabs.List>
        )}

        <Flex
          style={{
            backgroundColor: 'gray',
            borderRadius: 16,
            position: 'relative',
            overflow: 'hidden',
          }}
          flex={1}
          mih={300}
          p={'xxl'}
          bg={'gray.1'}
          direction={'column'}
          justify={'space-between'}
          align={'start'}
        >
          {renderTabContents(categoryInfo)}
          <Button onClick={handleAddNewClick}>Add new</Button>
        </Flex>
      </Tabs>

      <AssetUpdateChoiceModal
        opened={choiceModalOpened}
        onClose={() => setChoiceModalOpened(false)}
        selectedMethod={selectedMethod}
        onMethodChange={setSelectedMethod}
        onContinue={handleContinue}
      />

      <ProviderReferenceModal
        opened={providerModalOpened}
        onClose={handleProviderModalClose}
        onSubmit={handleProviderSubmit}
        isLoading={isUpserting || isLoadingLayout}
        clientId={clientId}
      />
    </Flex>
  );
};

type AssetUpdateChoiceModalProps = {
  opened: boolean;
  onClose: () => void;
  selectedMethod: 'connection' | 'manually';
  onMethodChange: (method: 'connection' | 'manually') => void;
  onContinue: () => void;
};

const AssetUpdateChoiceModal = ({
  opened,
  onClose,
  selectedMethod,
  onMethodChange,
  onContinue,
}: AssetUpdateChoiceModalProps) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="How would you like to add the data?"
      centered
      size="sm"
    >
      <Radio.Group
        value={selectedMethod}
        onChange={value => onMethodChange(value as 'connection' | 'manually')}
      >
        <Stack gap="lg" mb="xl">
          <Radio
            value="connection"
            label={
              <Box>
                <Text fw={500}>Connection</Text>
                <Text size="sm" c="dimmed">
                  Connect to the provider to automate data collection (usually updated daily)
                </Text>
              </Box>
            }
            styles={{ body: { alignItems: 'flex-start' }, inner: { marginTop: 4 } }}
          />

          <Divider />

          <Radio
            value="manually"
            label={
              <Box>
                <Text fw={500}>Manually</Text>
                <Text size="sm" c="dimmed">
                  Enter the data manually
                </Text>
              </Box>
            }
            styles={{ body: { alignItems: 'flex-start' }, inner: { marginTop: 4 } }}
          />
        </Stack>
      </Radio.Group>

      <Button fullWidth onClick={onContinue} size="md">
        Continue
      </Button>
    </Modal>
  );
};

type ProviderReferenceModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (providerReference: string) => void;
  isLoading?: boolean;
  clientId: string;
};

const ProviderReferenceModal = ({
  opened,
  onClose,
  onSubmit,
  isLoading = false,
  clientId,
}: ProviderReferenceModalProps) => {
  const { validateProviderReference } = useValidateProviderReference(clientId);
  const [isValidating, setIsValidating] = useState(false);
  const form = useForm<ProviderReferenceForm>({
    initialValues: {
      providerReference: '',
    },
    validate: zodResolver(providerReferenceSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  const handleClose = () => {
    if (!isLoading && !isValidating) {
      form.reset();
      onClose();
    }
  };

  const handleSubmit = async (values: ProviderReferenceForm) => {
    try {
      setIsValidating(true);

      const result = await validateProviderReference(values.providerReference);
      if (result.data.is_duplicate) {
        form.setFieldError('providerReference', 'This provider reference number is already in use');
        return;
      }

      onSubmit(values.providerReference);
      form.reset();
    } catch (error) {
      Logger.warn('Provider reference validation failed', { error });
      onSubmit(values.providerReference);
      form.reset();
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Connected data" centered size="sm">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Text size="sm" mb="lg" c="dimmed">
          Connect to the provider by entering their reference number
        </Text>
        <TextInput
          label="Provider reference number"
          placeholder="Enter reference number..."
          {...form.getInputProps('providerReference')}
          disabled={isLoading || isValidating}
          mb="xl"
        />
        <Button
          fullWidth
          type="submit"
          size="md"
          disabled={!form.isValid() || isLoading || isValidating}
          loading={isLoading || isValidating}
        >
          Continue
        </Button>
      </form>
    </Modal>
  );
};
